import { Capacitor } from '@capacitor/core'
import { browserProfilingIntegration, browserTracingIntegration, init } from '@sentry/vue'

import {
  diagnosticPrivacyIntegration,
  prepareDiagnosticEvent,
  prepareDiagnosticSpan,
} from '@/assets/composables/diagnostics'

// Typed independently of the JavaScript bootstrap so SDK migrations are checked.
export const createTelemetryOptions = (
  environment: string,
  production: boolean,
): NonNullable<Parameters<typeof init>[0]> => {
  // `environment` is only a Sentry label. Development/preview builds must never
  // use the production transport. Native debug apps can contain a production
  // web bundle, so also check the flag injected by Capacitor's native bridge.
  const enabled =
    production &&
    environment === 'production' &&
    globalThis.location?.hostname === 'ja.pelikan.sk' &&
    !(Capacitor.isNativePlatform() && Capacitor.DEBUG)

  return {
    enabled,
    release: `${__APP_NAME__}@${__APP_VERSION__}`,
    environment,
    dsn: enabled
      ? 'https://a8ae3094098c4712bdd67df1fd3f343f@o4507514363379712.ingest.de.sentry.io/4507514388545616'
      : undefined,
    attachProps: false,
    dataCollection: {
      userInfo: false,
      cookies: false,
      httpHeaders: false,
      httpBodies: [],
      urlQueryParams: false,
      graphQL: { document: false, variables: false },
      genAI: { inputs: false, outputs: false },
      mcp: { inputs: false, outputs: false },
      databaseQueryData: false,
      queues: false,
      stackFrameVariables: false,
      frameContextLines: 0,
    },
    beforeBreadcrumb: () => null,
    beforeSend: prepareDiagnosticEvent,
    beforeSendSpan: prepareDiagnosticSpan,
    integrations: (defaults) => [
      ...defaults.filter(
        (integration) =>
          !['Breadcrumbs', 'BrowserSession', 'Replay', 'Feedback'].includes(integration.name),
      ),
      browserTracingIntegration(),
      browserProfilingIntegration(),
      diagnosticPrivacyIntegration(),
    ],
    tracesSampleRate: enabled ? 0.5 : 0,
    tracePropagationTargets: [/^\//],
    profileSessionSampleRate: enabled ? 1 : 0,
    profileLifecycle: 'trace',
    // Replay is intentionally absent: it records application/profile DOM outside
    // the diagnostic allowlist, even when error events have been sanitized.
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
  }
}
